import React from 'react';
import { Save, Upload, CheckCircle, History, Play, Square, Lock, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface IDEToolbarProps {
  // Editor actions
  onSave: () => void;
  onPublish: () => void;
  onValidate: () => void;
  onToggleVersions: () => void;
  hasUnsavedChanges: boolean;
  isPublished: boolean;
  disabled: boolean;
  readOnly?: boolean;

  // Execution state
  mode: 'oneshot' | 'chat';
  onModeChange: (mode: 'oneshot' | 'chat') => void;
  isRunning: boolean;
  onRun: () => void;
  onStop: () => void;
  canRun: boolean;

  // Chat panel
  chatPanelOpen?: boolean;
  onToggleChatPanel?: () => void;

  // Content type (only agents can be run)
  contentType?: string;
}

export function IDEToolbar({
  onSave,
  onPublish,
  onValidate,
  onToggleVersions,
  hasUnsavedChanges,
  isPublished,
  disabled,
  readOnly = false,
  mode,
  onModeChange,
  isRunning,
  onRun,
  onStop,
  canRun,
  chatPanelOpen,
  onToggleChatPanel,
  contentType,
}: IDEToolbarProps) {
  const isAgent = contentType === 'agent';

  return (
    <div className="flex items-center gap-2 px-4 py-2 border-b bg-background">
      {/* Save/Publish Actions or Read-Only Badge */}
      {readOnly ? (
        <Badge variant="secondary" className="flex items-center gap-1">
          <Lock className="h-3 w-3" />
          Read-Only
        </Badge>
      ) : (
        <div className="flex gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant={hasUnsavedChanges ? 'default' : 'secondary'}
                onClick={onSave}
                disabled={disabled || !hasUnsavedChanges}
              >
                <Save className="h-4 w-4 mr-2" />
                Save Draft
              </Button>
            </TooltipTrigger>
            <TooltipContent>Save as Draft (Ctrl+S)</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="default"
                className="bg-green-600 hover:bg-green-700"
                onClick={onPublish}
                disabled={disabled}
              >
                <Upload className="h-4 w-4 mr-2" />
                Publish
              </Button>
            </TooltipTrigger>
            <TooltipContent>Publish (Ctrl+Shift+S)</TooltipContent>
          </Tooltip>
        </div>
      )}

      <Separator orientation="vertical" className="h-6" />

      {/* Validation */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button size="sm" variant="outline" onClick={onValidate} disabled={disabled}>
            <CheckCircle className="h-4 w-4 mr-2" />
            Validate
          </Button>
        </TooltipTrigger>
        <TooltipContent>Validate content</TooltipContent>
      </Tooltip>

      {/* Mode Toggle - only for agents */}
      {isAgent && (
        <>
          <Separator orientation="vertical" className="h-6" />
          <Tabs value={mode} onValueChange={(v) => onModeChange(v as 'oneshot' | 'chat')}>
            <TabsList className="h-8">
              <TabsTrigger value="oneshot" className="text-xs px-3" disabled={isRunning}>
                One-shot
              </TabsTrigger>
              <TabsTrigger value="chat" className="text-xs px-3" disabled={isRunning}>
                Interactive
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Chat panel toggle - show when in chat mode */}
          {mode === 'chat' && onToggleChatPanel && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant={chatPanelOpen ? 'secondary' : 'outline'}
                  onClick={onToggleChatPanel}
                  className={cn(chatPanelOpen && 'bg-blue-100 text-blue-700 hover:bg-blue-200')}
                >
                  <MessageCircle className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{chatPanelOpen ? 'Hide Chat' : 'Show Chat'}</TooltipContent>
            </Tooltip>
          )}
        </>
      )}

      <div className="flex-1" />

      {/* Run/Stop Button - only for agents */}
      {isAgent && (
        <>
          {isRunning ? (
            <Button variant="destructive" size="sm" onClick={onStop}>
              <Square className="h-4 w-4 mr-2" />
              Stop
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" onClick={onRun} disabled={!canRun || disabled}>
                  <Play className="h-4 w-4 mr-2" />
                  Run
                </Button>
              </TooltipTrigger>
              <TooltipContent>Run agent (Ctrl+Enter)</TooltipContent>
            </Tooltip>
          )}
          <Separator orientation="vertical" className="h-6" />
        </>
      )}

      {/* Version History Toggle */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button size="sm" variant="ghost" onClick={onToggleVersions} disabled={disabled}>
            <History className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Version History</TooltipContent>
      </Tooltip>
    </div>
  );
}

export default IDEToolbar;
